// Pure mappers from the per-site reference bundle to the `{ id, label }` option
// shape the flow-chart node selects expect. Kept side-effect-free so they can be
// unit-tested with a mock bundle (and so an empty/missing bundle degrades
// gracefully to an empty option list instead of crashing a node).

import type {
  APIAttribute,
  APIReferenceBundle,
} from '@/services/notificationService'

export type NodeOption = { id: string; label: string }

const byLabel = (a: NodeOption, b: NodeOption) =>
  a.label.localeCompare(b.label, 'he', { numeric: true })

// The editor works inside one site, so it does not offer conditions on other
// sites. A tree that already tests site keeps it.
const isOffered = (a: APIAttribute, selectedName?: string | null) =>
  a.name !== 'site_id' || a.name === selectedName

/** Attribute-category options for ConnectNode's first dropdown. */
export function attributeOptions(
  bundle: APIReferenceBundle,
  selectedName?: string | null
): NodeOption[] {
  return (bundle.attributes ?? [])
    .filter(a => isOffered(a, selectedName))
    .map(a => ({
      id: a.name,
      label: a.label || a.name,
    }))
    .sort(byLabel)
}

/** ConnectNode's chosen attribute; undefined when none is chosen or the bundle lacks it. */
export function findAttribute(
  bundle: APIReferenceBundle,
  attributeName: string | null | undefined
): APIAttribute | undefined {
  if (!attributeName) return undefined
  return (bundle.attributes ?? []).find(a => a.name === attributeName)
}

/** Allowed-value options for ConnectNode's second dropdown, for a chosen attribute. */
export function attributeValueOptions(
  bundle: APIReferenceBundle,
  attributeName: string | null | undefined
): NodeOption[] {
  const attribute = findAttribute(bundle, attributeName)
  return (attribute?.allowedValues ?? [])
    .map(v => ({
      id: v.value,
      label: v.label || v.value,
    }))
    .sort(byLabel)
}

/**
 * Device-alert-code options for MeterNode. Ids are the real codes; label falls back to the code.
 * A code that does not open alerts is offered only when the node already holds it.
 */
export function deviceAlertOptions(
  bundle: APIReferenceBundle,
  selectedCode?: string | null
): NodeOption[] {
  return (bundle.deviceAlertCodes ?? [])
    .filter(c => c.available !== false || c.code === selectedCode)
    .map(c => ({
      id: c.code,
      label: c.name ?? c.code,
    }))
}

/** True when the node holds a device-alert code that the Gateway marks as not opening alerts. */
export function isUnavailableDeviceAlert(
  bundle: APIReferenceBundle,
  code: string | null | undefined
): boolean {
  return (bundle.deviceAlertCodes ?? []).some(
    c => c.code === code && c.available === false
  )
}

/**
 * Notification-channel options for MessageNode. Ids are the numeric channel ids as strings.
 * A new choice is an enabled channel. A disabled channel appears only when the
 * tree already names it (`selectedIds`), marked as disabled, so the editor keeps it.
 */
export function channelOptions(
  bundle: APIReferenceBundle,
  selectedIds: readonly string[] = []
): NodeOption[] {
  return (bundle.notificationChannels ?? []).flatMap(c => {
    const id = String(c.notificationChannelId)
    const label = c.name || id
    if (c.isEnabled !== false) return [{ id, label }]
    return selectedIds.includes(id) ? [{ id, label: `${label} (מושבת)` }] : []
  })
}

/** Every listed channel id, disabled ones too. Undefined when nothing is listed, so no id can be checked. */
export function knownChannelIds(
  bundle: APIReferenceBundle
): ReadonlySet<number> | undefined {
  const ids = (bundle.notificationChannels ?? []).map(
    c => c.notificationChannelId
  )
  return ids.length > 0 ? new Set(ids) : undefined
}

/** Channel ids by channel key. A channel the Gateway sent without a key is left out. */
export function channelIdsByKey(
  bundle: APIReferenceBundle
): ReadonlyMap<string, number> {
  return new Map(
    (bundle.notificationChannels ?? []).flatMap(c =>
      c.notificationChannelKey
        ? [[c.notificationChannelKey, c.notificationChannelId] as const]
        : []
    )
  )
}
